import React, { useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { AddProjectReport } from "../../Redux/Actions/projectReportActions";
import ProjectReportDropdown from "./projectReportDropdown";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";
import "./addProjectReport.css";

const SCORE_FIELDS = [
    { key: "location_advantage", label: "Location Advantage" },
    { key: "future_growth", label: "Future Growth" },
    { key: "trust_score", label: "Trust Score" },
    { key: "Livability_score", label: "Livability Score" },
    { key: "assurance_score", label: "Assurance Score" },
];

const emptyScores = SCORE_FIELDS.reduce((acc, { key }) => {
    acc[key] = { value: "", note: [""] };
    return acc;
}, {});

const AddProjectReportPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [selectedProject, setSelectedProject] = useState("");
    const [scores, setScores] = useState(emptyScores);
    const [reportPdf, setReportPdf] = useState({ value: "", note: [""] });
    const [pdfUploading, setPdfUploading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const updateScoreValue = (key, value) => {
        if (value !== "" && (Number(value) < 0 || Number(value) > 100)) return;
        setScores((prev) => ({
            ...prev,
            [key]: { ...prev[key], value },
        }));
    };

    const addScoreNote = (key) =>
        setScores((prev) => ({
            ...prev,
            [key]: { ...prev[key], note: [...prev[key].note, ""] },
        }));

    const removeScoreNote = (key, idx) =>
        setScores((prev) => ({
            ...prev,
            [key]: { ...prev[key], note: prev[key].note.filter((_, i) => i !== idx) },
        }));

    const updateScoreNote = (key, idx, value) =>
        setScores((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                note: prev[key].note.map((n, i) => (i === idx ? value : n)),
            },
        }));

    const addPdfNote = () =>
        setReportPdf((prev) => ({ ...prev, note: [...prev.note, ""] }));

    const removePdfNote = (idx) =>
        setReportPdf((prev) => ({ ...prev, note: prev.note.filter((_, i) => i !== idx) }));

    const updatePdfNote = (idx, value) =>
        setReportPdf((prev) => ({
            ...prev,
            note: prev.note.map((n, i) => (i === idx ? value : n)),
        }));

    const uploadReportPdf = async ({ file, onSuccess, onError }) => {
        if (file.type !== "application/pdf") {
            message.error("Only PDF files are allowed!");
            onError(new Error("Invalid file type"));
            return;
        }
        setPdfUploading(true);
        try {
            const url = await AddImage(file);
            onSuccess({ url }, file);
            setReportPdf((prev) => ({ ...prev, value: url }));
        } catch (err) {
            message.error("PDF upload failed!");
            onError(err);
        } finally {
            setPdfUploading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitted(true);
        if (!selectedProject) {
            toast.error("Please select a project");
            return;
        }
        if (!reportPdf.value) {
            toast.error("Please upload the report PDF");
            return;
        }

        const payload = {
            project: selectedProject,
            location_advantage: {
                value: Number(scores.location_advantage.value) || 0,
                note: scores.location_advantage.note.filter((n) => n?.trim()),
            },
            future_growth: {
                value: Number(scores.future_growth.value) || 0,
                note: scores.future_growth.note.filter((n) => n?.trim()),
            },
            trust_score: {
                value: Number(scores.trust_score.value) || 0,
                note: scores.trust_score.note.filter((n) => n?.trim()),
            },
            Livability_score: {
                value: Number(scores.Livability_score.value) || 0,
                note: scores.Livability_score.note.filter((n) => n?.trim()),
            },
            assurance_score: {
                value: Number(scores.assurance_score.value) || 0,
                note: scores.assurance_score.note.filter((n) => n?.trim()),
            },
            // risk_score: {
            //     value: Number(scores.risk_score.value) || 0,
            //     note: scores.risk_score.note.filter((n) => n?.trim()),
            // },
            report_pdf: {
                value: reportPdf.value,
                note: reportPdf.note.filter((n) => n?.trim()),
            },
        };

        try {
            setLoading(true);
            const result = await dispatch(AddProjectReport(payload));
            if (result) {
                toast.success("Project report added successfully");
                navigate("/project_report");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to add project report");
        } finally {
            setLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="ab2-wrapper">

                <div className="ab2-header">
                    <div className="ab2-header-left">
                        <div className="ab2-header-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="ab2-title">Add Project Report</h1>
                            <p className="ab2-subtitle">Create a new project report</p>
                        </div>
                    </div>
                    <button className="ab2-view-btn" onClick={() => navigate("/project_report")}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        View Report List
                    </button>
                </div>

                <div className="ab2-body">

                    <div className="ab2-main">

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                Select Project <span style={{ color: "red" }}>*</span>
                            </h3>
                            <div className="ab2-field">
                                <ProjectReportDropdown
                                    value={selectedProject}
                                    onChange={(e) => setSelectedProject(e.target.value)}
                                />
                                {submitted && !selectedProject && (
                                    <span className="ab2-error-msg">Project is required</span>
                                )}
                            </div>
                        </div>

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 3v18h18" /><path d="M18 17V9M13 17V5M8 17v-3" />
                                </svg>
                                Report Scores
                            </h3>

                            {SCORE_FIELDS.map(({ key, label }) => (
                                <div key={key} className="ab2-content-block">
                                    <div className="ab2-block-header">
                                        <span className="ab2-block-label">{label}</span>
                                    </div>
                                    <div className="ab2-field">
                                        <input
                                            type="number"
                                            className="ab2-input"
                                            min={0}
                                            max={100}
                                            value={scores[key].value}
                                            onChange={(e) => updateScoreValue(key, e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            placeholder={`Enter ${label.toLowerCase()}`}
                                        />
                                    </div>

                                    {scores[key].note.map((note, idx) => (
                                        <div key={idx} className="ab2-field" style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 8 }}>
                                            <textarea
                                                className="ab2-textarea"
                                                rows={2}
                                                value={note}
                                                onChange={(e) => updateScoreNote(key, idx, e.target.value)}
                                                placeholder={`Note ${idx + 1} for ${label}`}
                                            />
                                            {scores[key].note.length > 1 && (
                                                <button type="button" className="ab2-block-remove" onClick={() => removeScoreNote(key, idx)}>
                                                    <DeleteOutlined />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" className="ab2-add-block-btn" style={{ marginTop: 8 }} onClick={() => addScoreNote(key)}>
                                        <PlusOutlined /> Add Note
                                    </button>
                                </div>
                            ))}
                        </div>

                    </div>

                    <div className="ab2-sidebar">

                        <div className="ab2-card">
                            <h3 className="ab2-card-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                                </svg>
                                Report PDF <span style={{ color: "red" }}>*</span>
                            </h3>
                            <p className="ab2-hint">Upload the report PDF file</p>

                            {reportPdf.value ? (
                                <div className="ab2-section-img-wrap" style={{ display: "block", padding: 12, width: "100%" }}>
                                    <a href={reportPdf.value} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#0D1A52", fontWeight: 600, wordBreak: "break-all" }}>
                                        View Uploaded PDF
                                    </a>
                                    <div>
                                        <button type="button" className="ab2-section-img-remove" onClick={() => setReportPdf((prev) => ({ ...prev, value: "" }))}>
                                            <DeleteOutlined /> Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <Upload customRequest={uploadReportPdf} showUploadList={false} accept="application/pdf">
                                    <button type="button" className={`ab2-upload-btn ${submitted && !reportPdf.value ? "ab2-input--error" : ""}`}>
                                        {pdfUploading ? <LoadingOutlined /> : <PlusOutlined />}
                                        {pdfUploading ? "Uploading…" : "Upload Report PDF"}
                                    </button>
                                </Upload>
                            )}
                            {submitted && !reportPdf.value && <span className="ab2-error-msg">Report PDF is required</span>}

                            {reportPdf.note.map((note, idx) => (
                                <div key={idx} className="ab2-field" style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10 }}>
                                    <textarea
                                        className="ab2-textarea"
                                        rows={2}
                                        value={note}
                                        onChange={(e) => updatePdfNote(idx, e.target.value)}
                                        placeholder={`Note ${idx + 1} for Report PDF`}
                                    />
                                    {reportPdf.note.length > 1 && (
                                        <button type="button" className="ab2-block-remove" onClick={() => removePdfNote(idx)}>
                                            <DeleteOutlined />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button type="button" className="ab2-add-block-btn" style={{ marginTop: 8 }} onClick={addPdfNote}>
                                <PlusOutlined /> Add Note
                            </button>
                        </div>

                        <div className="ab2-actions">
                            <button type="button" className="ab2-btn ab2-btn--cancel" onClick={() => navigate("/project_report")} disabled={loading}>
                                Cancel
                            </button>
                            <button type="button" className="ab2-btn ab2-btn--submit" onClick={handleSubmit} disabled={loading || pdfUploading}>
                                {loading ? (
                                    <><LoadingOutlined /> Saving…</>
                                ) : (
                                    <>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        Save Report
                                    </>
                                )}
                            </button>
                        </div>

                    </div>

                </div>
            </div>
        </MainLayout>
    );
};

export default AddProjectReportPage;